import { baseApi } from "./baseApi";

export interface Setting {
  key: string;
  value: string;
  category: string;
  description?: string;
}

export interface SettingsByCategory {
  [category: string]: Setting[];
}

export interface PricingSettings {
  deliveryFee: number;
  taxRate: number;
}

export interface BusinessSettings {
  name: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  socialMedia?: Record<string, string>;
  businessHours?: Record<string, string>;
  description?: string;
}

export interface PaymentSettings {
  paystackEnabled: boolean;
  flutterwaveEnabled: boolean;
}

export interface LegalSettings {
  privacyPolicy: string;
  termsOfService: string;
  refundPolicy: string;
}

export interface UpdateSettingRequest {
  key: string;
  value: string;
}

export interface UpdateSettingsRequest {
  updates: UpdateSettingRequest[];
}

/**
 * Settings API slice
 */
export const settingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all settings (admin only)
    getSettings: builder.query<
      { success: boolean; data: SettingsByCategory },
      void
    >({
      query: () => "/settings",
      providesTags: ["Settings"],
    }),

    // Get settings by category (admin only)
    getSettingsByCategory: builder.query<
      { success: boolean; data: Setting[] },
      string
    >({
      query: (category) => `/settings/${category}`,
      providesTags: ["Settings"],
    }),

    // Update settings (bulk update, admin only)
    updateSettings: builder.mutation<
      { success: boolean; message?: string },
      UpdateSettingsRequest
    >({
      query: (data) => ({
        url: "/settings",
        method: "PUT",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Settings"],
    }),

    // Get public business settings (no auth required)
    getPublicSettings: builder.query<
      { success: boolean; data: Partial<BusinessSettings> },
      void
    >({
      query: () => ({
        url: "/settings/public",
        credentials: "omit", // Public endpoint, no auth needed
      }),
      providesTags: ["PublicSettings"],
    }),

    // Get public pricing settings (no auth required)
    getPublicPricingSettings: builder.query<
      { success: boolean; data: PricingSettings },
      void
    >({
      query: () => ({
        url: "/settings/public/pricing",
        credentials: "omit", // Public endpoint, no auth needed
      }),
      providesTags: ["PublicSettings"],
    }),

    // Get public legal settings (no auth required)
    getPublicLegalSettings: builder.query<
      { success: boolean; data: Partial<LegalSettings> },
      void
    >({
      query: () => ({
        url: "/settings/public/legal",
        credentials: "omit", // Public endpoint, no auth needed
      }),
      providesTags: ["PublicSettings"],
    }),

    // Initialize default settings (admin only, one-time setup)
    initializeSettings: builder.mutation<
      { success: boolean; message?: string },
      void
    >({
      query: () => ({
        url: "/settings/initialize",
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: ["Settings"],
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useGetSettingsByCategoryQuery,
  useUpdateSettingsMutation,
  useGetPublicSettingsQuery,
  useGetPublicPricingSettingsQuery,
  useGetPublicLegalSettingsQuery,
  useInitializeSettingsMutation,
} = settingsApi;
